import { useContext } from 'react';
import { Box, Grid2, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { isNil } from 'lodash';
import { DATE_FORMATS } from '@/consts/date.consts';
import { formatInGMT } from '@/utils/date.utils';
import { ButtonTextPlain } from '@/UI/ButtonTextPlain/ButtonTextPlain';
import { color } from '@/colors';
import { Label } from '@/UI/Label/Label';
import { FlexCenVer, FlexCol } from '@/UI/styles';
import { I18N_KEYS } from '@/language/keys';
import { formatNumberWithCommas } from '@/utils/numbers';
import { PermissionsContainer } from '@/components/PermissionsContainer/PermissionsContainer';
import { AsteriskIcon } from '@/UI/Icons/AsteriskIcon';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { SubscriptionType } from '@/types/shared';
import { CreditsContext } from '@/services/CreditsContext';
import { Tag } from '@/UI/Tag/Tag';
import { InfoIcon } from '@/UI/Icons';
import { useConfirmationDialogStore } from '@/state/confirmation-dialog.state';
import { useAnalytics } from '@/hooks/useAnalytics';
import { CANCEL_PLAN_EVENT_NAMES } from '@/consts/analytics.consts';
import { useSubscriptionPlan } from './hooks/useSubscriptionPlan';

export const PlanSection = () => {
  const { t } = useTranslation();
  const { openUpgradeModal } = useContext(CreditsContext);
  const { openConfirmationDialog } = useConfirmationDialogStore();
  const {
    customerPortalSession,
    planName,
    usedSeats,
    seats,
    monthlyAllocatedCredits,
    creditsCanRollover,
    date,
    dateLabel,
    scheduledDowngradePlan,
    scheduledDowngradeDate,
    subscriptionType,
  } = useSubscriptionPlan();

  const { track } = useAnalytics();

  // Free plan can have a payments link if they had a subscription in the past and cancelled
  const isFirstTimeFree = subscriptionType === SubscriptionType.Free && !customerPortalSession;

  const getPlanCta = () => {
    return (
      <PermissionsContainer permission="plan" type="action">
        <Box sx={{ justifySelf: 'flex-end' }}>
          {subscriptionType === SubscriptionType.Team ? (
            <ButtonContained mode="outlined" onClick={openUpgradeModal} size="xs">
              {t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.CHANGE_PLAN)}
            </ButtonContained>
          ) : (
            <ButtonContained onClick={openUpgradeModal} mode="filled-light-secondary" size="xs">
              {t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.UPGRADE)}
            </ButtonContained>
          )}
        </Box>
      </PermissionsContainer>
    );
  };

  const openCustomerPortal = () => {
    window.open(customerPortalSession, '_blank', 'noreferrer=yes,noopener=yes');
  };

  const openCancelPlanDialog = () => {
    track(CANCEL_PLAN_EVENT_NAMES.DIALOG_OPENED, { subscriptionType });

    openConfirmationDialog({
      title: t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.CANCEL_PLAN_DIALOG.TITLE),
      message: t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.CANCEL_PLAN_DIALOG.MESSAGE, {
        context: creditsCanRollover ? 'rollover' : 'no_rollover',
        end_date: date ? formatInGMT(new Date(date), DATE_FORMATS.DATE) : '',
      }),
      confirmBtnText: t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.CANCEL_PLAN_DIALOG.CONFIRM),
      cancelBtnText: t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.CANCEL_PLAN_DIALOG.CANCEL),
      onConfirm: () => {
        track(CANCEL_PLAN_EVENT_NAMES.CONFIRMED, { subscriptionType });
        openCustomerPortal();
      },
      onClose: () => {
        track(CANCEL_PLAN_EVENT_NAMES.KEPT_PLAN, { subscriptionType });
      },
    });
  };

  const getManagePaymentsCta = () => {
    return (
      <PermissionsContainer permission="managePayments" type="action">
        <FlexCenVer sx={{ gap: 2 }}>
          <ButtonContained mode="outlined" onClick={openCustomerPortal} size="xs" disabled={!customerPortalSession}>
            {t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.MANAGE_BILLING)}
          </ButtonContained>
          {subscriptionType !== SubscriptionType.Free && (
            <ButtonTextPlain
              variant="body-sm-rg"
              color={color.White80_T}
              onClick={openCancelPlanDialog}
              disabled={!customerPortalSession}
            >
              {t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.CANCEL_PLAN)}
            </ButtonTextPlain>
          )}
        </FlexCenVer>
      </PermissionsContainer>
    );
  };

  return (
    <Grid2 container display="grid" direction="row" gridTemplateColumns="1fr 1.5fr 1fr" gap={4}>
      <Typography variant="body-lg-md">{t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.PLAN.PLAN)}</Typography>
      <FlexCol sx={{ gap: 2.5 }}>
        <FlexCol>
          <Label>{t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.PLAN.PLAN)}</Label>
          <Typography variant="body-std-md">{planName}</Typography>
        </FlexCol>
        {seats && usedSeats ? (
          <PermissionsContainer permission="seats">
            <FlexCol>
              <Label>{t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.PLAN.SEATS)}</Label>
              <Typography variant="body-std-md">
                {`${usedSeats} / ${seats}`} {t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.PLAN.SEATS_SUFFIX)}
              </Typography>
            </FlexCol>
          </PermissionsContainer>
        ) : null}
        {date && (
          <FlexCol>
            <FlexCenVer sx={{ gap: 0.5 }}>
              <Label>{dateLabel}</Label>
              {scheduledDowngradeDate && (
                <Tag
                  sx={{ mt: 0.25 }}
                  disableTextTransform
                  text={t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.PLAN.DOWNGRADE_TEXT, {
                    plan: scheduledDowngradePlan,
                  })}
                  bgColor={color.Yellow16_T}
                  textColor={color.Yellow100}
                />
              )}
            </FlexCenVer>
            <Typography variant="body-std-md">{formatInGMT(new Date(date), DATE_FORMATS.DATE_TIME_GMT)}</Typography>
          </FlexCol>
        )}
        {!isNil(monthlyAllocatedCredits) && (
          <PermissionsContainer permission="credits">
            <FlexCol>
              <FlexCenVer sx={{ gap: 0.5 }}>
                <Label>{t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.PLAN.MONTHLY_CREDITS)}</Label>
                <Tooltip
                  title={
                    creditsCanRollover
                      ? t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.PLAN.MONTHLY_CREDITS_TOOLTIP_ROLLOVER)
                      : t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.PLAN.MONTHLY_CREDITS_TOOLTIP_RESET)
                  }
                  placement="right"
                >
                  <InfoIcon width={16} height={16} />
                </Tooltip>
              </FlexCenVer>
              <FlexCenVer>
                <AsteriskIcon />
                <Typography variant="body-std-md">{formatNumberWithCommas(monthlyAllocatedCredits)}</Typography>
              </FlexCenVer>
            </FlexCol>
          </PermissionsContainer>
        )}
        {!isFirstTimeFree && getManagePaymentsCta()}
      </FlexCol>
      {getPlanCta()}
    </Grid2>
  );
};
